import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users } from "lucide-react";
import { formatCurrency } from "@/lib/config";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  AreaChart, Area, PieChart, Pie, Cell,
} from "recharts";

const MOIS_COURT = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];

export function SectionGraphiques({
  deptView, year, month, periodView,
  chartVentes, deptObjCA, monthlyData, deptData,
  tauxSignatureData,
  MONTHS, CHART_COLORS, DEPT_COLORS,
  canDo,
}) {
  return (
    <>
      {/* Taux de signature */}
      {deptView !== 'LMDS' && deptView !== 'LOCATION' && canDo('dashboard_taux_signature') && tauxSignatureData && tauxSignatureData.total_soumissions > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-600" />
              Taux de signature {year}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(tauxSignatureData.par_representant || {})
                .filter(([nom]) => nom && nom !== 'null' && nom !== 'undefined' && nom.trim() !== '')
                .sort((a, b) => b[1].taux - a[1].taux)
                .map(([nom, data]) => {
                  const taux = data.taux || 0;
                  const color = taux >= 40 ? "emerald" : taux >= 25 ? "amber" : "red";
                  return (
                    <div key={nom} className={`p-3 rounded-lg bg-${color}-50 border border-${color}-200`}>
                      <p className="text-sm font-semibold text-gray-900 truncate">{nom}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className={`text-xl font-bold text-${color}-700`}>{taux.toFixed(0)}%</p>
                        <p className="text-xs text-gray-500">{data.signees} / {data.soumissions} soumissions</p>
                      </div>
                      <div className="w-full bg-white rounded-full h-1.5 mt-1.5">
                        <div className={`h-1.5 rounded-full bg-${color}-500`} style={{ width: `${Math.min(100, taux)}%` }} />
                      </div>
                    </div>
                  );
                })}
            </div>
            <div className="mt-3 pt-3 border-t flex items-center justify-between text-sm">
              <span className="text-gray-500">Global: {tauxSignatureData.total_signees} / {tauxSignatureData.total_soumissions} soumissions</span>
              <span className="font-bold text-purple-700">{tauxSignatureData.taux_global}%</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Graphiques */}
      {canDo('dashboard_graphiques') && <>
      <div className={`grid grid-cols-1 ${deptView === 'global' ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-1'} gap-6`}>
        {deptView !== 'LMDS' && deptView !== 'LOCATION' && (
        <Card className={deptView === 'global' ? 'lg:col-span-2' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Ventes signées vs Revenus encaissés {deptView !== 'global' ? deptView + ' ' : ''}— {year}</CardTitle>
            <CardDescription>Ventes = contrats signés. Revenus = argent encaissé (QuickBooks).</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-56" data-testid="ventes-revenus-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartVentes} barGap={2}>
                  <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v, name) => [formatCurrency(v), name === 'ventes' ? 'Ventes signées' : name === 'revenus' ? 'Revenus encaissés' : 'Objectif CA']} />
                  <Legend formatter={v => v === 'ventes' ? 'Ventes signées' : v === 'revenus' ? 'Revenus encaissés' : 'Objectif CA'} />
                  {deptObjCA > 0 && <Bar dataKey="objectif" name="objectif" fill="#bfdbfe" radius={[3, 3, 0, 0]} />}
                  <Bar dataKey="ventes" name="ventes" fill="#059669" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="revenus" name="revenus" fill="#6366f1" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        )}
        {deptView === 'global' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Revenus par département</CardTitle>
            <CardDescription>{MONTHS[month]} {year}</CardDescription>
          </CardHeader>
          <CardContent>
            {deptData.length === 0 ? (
              <p className="text-sm text-gray-400 italic py-8 text-center">Pas de données pour ce mois</p>
            ) : (
              <div className="h-56" data-testid="dept-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={deptData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                      {deptData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={DEPT_COLORS[index % DEPT_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-3 mt-1">
                  {deptData.map((d, i) => (
                    <div key={d.dept} className="flex items-center gap-1.5 text-xs">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: DEPT_COLORS[i] }}></div>
                      <span>{d.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Evolution mensuelle — Revenus & Depenses {deptView !== 'global' ? deptView + ' ' : ''}{year}</CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            const hasRevenueData = monthlyData.some(m => (m.revenus || 0) > 0 || (m.depenses || 0) > 0);
            if (!hasRevenueData) {
              return (
                <div className="h-56 flex flex-col items-center justify-center text-center" data-testid="monthly-chart-empty">
                  <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <p className="text-sm font-medium text-slate-600">En attente de connexion QuickBooks</p>
                  <p className="text-xs text-slate-400 mt-1 max-w-xs">Les revenus et depenses seront affiches ici une fois QuickBooks reconnecte. Ce tableau utilise les donnees bancaires reelles.</p>
                </div>
              );
            }
            return (
              <div className="h-56" data-testid="monthly-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyData}>
                    <defs>
                      <linearGradient id="colorRevenus2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={CHART_COLORS.revenus} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={CHART_COLORS.revenus} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorDepenses2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={CHART_COLORS.depenses} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={CHART_COLORS.depenses} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="mois_nom" tick={{ fontSize: 11 }} tickFormatter={v => (v || "").substring(0, 3)} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v / 1000}k`} />
                    <Tooltip formatter={(v, name) => [formatCurrency(v), name === 'revenus' ? 'Revenus' : 'Depenses']} />
                    <Area type="monotone" dataKey="revenus" name="revenus" stroke={CHART_COLORS.revenus} fillOpacity={1} fill="url(#colorRevenus2)" strokeWidth={2} />
                    <Area type="monotone" dataKey="depenses" name="depenses" stroke={CHART_COLORS.depenses} fillOpacity={1} fill="url(#colorDepenses2)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            );
          })()}
        </CardContent>
      </Card>
      </>}
    </>
  );
}
